/** Metadata only: executable content and execution receipts never enter a menu. */
export interface SkillShortcut {
  skillKey: string;
  commandKey: string;
  name: string;
  description: string;
  alias: string | null;
  version: string;
  required: boolean;
}

export interface SkillShortcutPage {
  schemaVersion: 1;
  revision: string;
  items: SkillShortcut[];
  nextPage: number | null;
}

function cleanText(value: unknown, max: number): value is string {
  return (
    typeof value === 'string' &&
    value.length <= max &&
    Array.from(value).every(
      char => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127,
    )
  );
}

function exactKeys(value: Record<string, unknown>, keys: string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every(key => key in value)
  );
}

export function isSkillShortcut(value: unknown): value is SkillShortcut {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  return (
    exactKeys(item, [
      'skillKey',
      'commandKey',
      'name',
      'description',
      'alias',
      'version',
      'required',
    ]) &&
    cleanText(item.skillKey, 200) &&
    item.skillKey.length > 0 &&
    cleanText(item.commandKey, 128) &&
    /^[a-z0-9][a-z0-9-]*$/.test(item.commandKey) &&
    cleanText(item.name, 200) &&
    cleanText(item.description, 500) &&
    (item.alias === null || cleanText(item.alias, 200)) &&
    cleanText(item.version, 100) &&
    typeof item.required === 'boolean'
  );
}

export function isSkillShortcutPage(
  value: unknown,
): value is SkillShortcutPage {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const page = value as Record<string, unknown>;
  return (
    exactKeys(page, ['schemaVersion', 'revision', 'items', 'nextPage']) &&
    page.schemaVersion === 1 &&
    typeof page.revision === 'string' &&
    /^[a-f0-9]{64}$/.test(page.revision) &&
    Array.isArray(page.items) &&
    page.items.length <= 100 &&
    page.items.every(isSkillShortcut) &&
    new Set(page.items.map(item => item.commandKey)).size ===
      page.items.length &&
    (page.nextPage === null ||
      (Number.isSafeInteger(page.nextPage) && Number(page.nextPage) > 1))
  );
}

export type SkillIntentResolution =
  | { kind: 'ambiguous'; options: SkillShortcut[] }
  | { kind: 'required'; plan: import('./skill-vault').ResolvedSkillPlan }
  | {
      kind: 'optional';
      context: import('./skill-vault').SkillPlanContext;
      recommendation: import('./skill-vault').OptionalSkillRecommendation;
    };

/** Receipt stays outside model-visible output; refs contain no skill body. */
export interface ProtectedOptionalSkillExecution {
  invocationId: string;
  activityId: string;
  bindingId: string;
  phase: string;
  phaseAttemptId: string;
  skillKey: string;
  releaseId: string;
  sha256: string;
  receipt: string;
  confirmedAt: string;
}

export interface SkillShortcutEvidence {
  state: 'prepared' | 'pending' | 'confirmed' | 'unsupported' | 'unavailable';
  revision: string | null;
  nativeSessionId: string | null;
  observedAt: string | null;
}
export function isSkillShortcutEvidence(
  value: unknown,
): value is SkillShortcutEvidence {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  return (
    exactKeys(item, ['state', 'revision', 'nativeSessionId', 'observedAt']) &&
    ['prepared', 'pending', 'confirmed', 'unsupported', 'unavailable'].includes(
      String(item.state),
    ) &&
    (item.revision === null ||
      (typeof item.revision === 'string' &&
        /^[a-f0-9]{64}$/.test(item.revision))) &&
    (item.nativeSessionId === null ||
      (cleanText(item.nativeSessionId, 255) &&
        item.nativeSessionId.length > 0)) &&
    (item.observedAt === null ||
      (typeof item.observedAt === 'string' &&
        Number.isFinite(Date.parse(item.observedAt)))) &&
    (item.state !== 'confirmed' ||
      (item.revision !== null &&
        item.nativeSessionId !== null &&
        item.observedAt !== null))
  );
}

export interface RefreshIntegrationAssetsRequest {
  bundleVersion: string;
  assetManifestDigest: string;
  sessionContext: import('./session-binding').SessionBindingContext;
}
